import { AffirmLogo } from '@jouzen/ecom-components/icons/AffirmLogo';
import { AmericanExpressLogo } from '@jouzen/ecom-components/icons/AmericanExpressLogo';
import { ApplePayLogo } from '@jouzen/ecom-components/icons/ApplePayLogo';
import { GooglePayLogo } from '@jouzen/ecom-components/icons/GooglePayLogo';
import { HsaFsaBlockLogo } from '@jouzen/ecom-components/icons/HsaFsaBlockLogo';
import { KlarnaLogo } from '@jouzen/ecom-components/icons/KlarnaLogo';
import { MasterCardLogo } from '@jouzen/ecom-components/icons/MasterCardLogo';
import { PaypalLogo } from '@jouzen/ecom-components/icons/PaypalLogo';
import { VisaLogo } from '@jouzen/ecom-components/icons/VisaLogo';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import HsaFsaIcon from '@/app/[locale]/_components/HomeHero/_heros/common/HsaFsaIcon';
import { useMultivariateAnimationConfig } from '@/app/experiments/MultivariatePayments/useMultivariateAnimationConfig';
import useUsaOnly from '@/app/hooks/useUsaOnly';

import CyclingElementAnimator from '../CyclingElementAnimator';
import RichText from '../RichText';
import MultivariatePill from './MultivariatePill';

interface PillsWithTitleProps {
  readonly children: React.ReactNode;
  readonly title: MessageKey;
}

const PillsWithTitle = ({ children, title }: PillsWithTitleProps) => {
  const t = useTranslations();
  return (
    <div className="flex w-full flex-row items-center justify-center gap-2">
      {/* Display title last, but screen reader should read it before children */}
      <Typography className="order-last font-normal text-sandstone-200">
        {t(title)}
      </Typography>
      {children}
    </div>
  );
};

const MultivariatePaymentOptions = () => {
  const isUS = useUsaOnly();
  const showKlarna = useGateValue('show_klarna_logo');

  const { cycleDuration, transitionDuration } =
    useMultivariateAnimationConfig();

  return (
    <>
      <Typography className="sr-only" Element="h2">
        <RichText messageKey="payment_options" />
      </Typography>

      {/* Mobile */}
      <div className="flex h-12 items-center justify-center text-sandstone-200 lg:hidden">
        <CyclingElementAnimator
          cycleDuration={cycleDuration}
          transitionDuration={transitionDuration}
          elements={[
            ...(isUS
              ? [
                  <HsaFsaIcon key="mvp-mobile-hsa-fsa" />,
                  <PillsWithTitle
                    key="mvp-mobile-affirm"
                    title="flexible_financing"
                  >
                    <MultivariatePill classname="w-14">
                      <>
                        <AffirmLogo className="h-4" />
                        <span className="sr-only">
                          <RichText messageKey="affirm" />
                        </span>
                      </>
                    </MultivariatePill>
                  </PillsWithTitle>,
                ]
              : []),
            ...(showKlarna
              ? [
                  <PillsWithTitle
                    key="mvp-mobile-klarna"
                    title="flexible_financing"
                  >
                    <MultivariatePill classname="w-14 bg-[#e7a7c6]! h-fit! py-1! rounded-lg!">
                      <>
                        <KlarnaLogo
                          className="h-4 text-[#0B051D]"
                          fill="currentColor"
                        />
                        <span className="sr-only">
                          <RichText messageKey="klarna" />
                        </span>
                      </>
                    </MultivariatePill>
                  </PillsWithTitle>,
                ]
              : []),
            <PillsWithTitle
              key="mvp-mobile-instant-checkout"
              title="instant_checkout"
            >
              <MultivariatePill>
                <>
                  <PaypalLogo className="h-8" />
                  <span className="sr-only">
                    <RichText messageKey="paypal" />
                  </span>
                </>
              </MultivariatePill>
              <MultivariatePill classname="border-2 border-black">
                <>
                  <ApplePayLogo className="h-[18px]" />
                  <span className="sr-only">
                    <RichText messageKey="apple_pay" />
                  </span>
                </>
              </MultivariatePill>
              <MultivariatePill classname="border-2 border-[#34383b] rounded-full!">
                <>
                  <GooglePayLogo className="h-5" />
                  <span className="sr-only">
                    <RichText messageKey="google_pay" />
                  </span>
                </>
              </MultivariatePill>
            </PillsWithTitle>,
            <PillsWithTitle
              key="mvp-mobile-major-cards-accepted"
              title="major_cards_accepted"
            >
              <MultivariatePill>
                <>
                  <VisaLogo className="h-10 w-10 text-[#172b85]" />
                  <span className="sr-only">
                    <RichText messageKey="visa" />
                  </span>
                </>
              </MultivariatePill>
              <MultivariatePill>
                <>
                  <MasterCardLogo className="h-6" />
                  <span className="sr-only">
                    <RichText messageKey="mastercard" />
                  </span>
                </>
              </MultivariatePill>
              <MultivariatePill classname="bg-[#1F72CD]! px-1!">
                <>
                  <AmericanExpressLogo className="h-12 w-12 text-white" />
                  <span className="sr-only">
                    <RichText messageKey="amex" />
                  </span>
                </>
              </MultivariatePill>
            </PillsWithTitle>,
          ]}
        />
      </div>

      {/* Desktop */}
      <div
        className="hidden flex-row items-center gap-2 text-black lg:flex"
        data-cy="multivariate-payment-options"
      >
        {isUS && (
          <>
            <MultivariatePill>
              <>
                <HsaFsaBlockLogo className="h-10" />
                <span className="sr-only">
                  <RichText messageKey="hsa_fsa_eligible_copy" />
                </span>
              </>
            </MultivariatePill>
            <MultivariatePill>
              <>
                <AffirmLogo className="h-4" />
                <span className="sr-only">
                  <RichText messageKey="affirm" />
                </span>
              </>
            </MultivariatePill>
          </>
        )}
        {showKlarna && (
          <MultivariatePill classname="bg-[#e7a7c6]! rounded-lg!">
            <>
              <KlarnaLogo
                className="h-6 w-14 text-[#0B051D]"
                fill="currentColor"
              />
              <span className="sr-only">
                <RichText messageKey="klarna" />
              </span>
            </>
          </MultivariatePill>
        )}
        <MultivariatePill>
          <>
            <PaypalLogo className="h-8" />
            <span className="sr-only">
              <RichText messageKey="paypal" />
            </span>
          </>
        </MultivariatePill>
        <MultivariatePill classname="border-2 border-black">
          <>
            <ApplePayLogo className="h-[18px]" />
            <span className="sr-only">
              <RichText messageKey="apple_pay" />
            </span>
          </>
        </MultivariatePill>
        <MultivariatePill classname="border-2 border-[#34383b] rounded-full!">
          <>
            <GooglePayLogo className="h-5" />
            <span className="sr-only">
              <RichText messageKey="google_pay" />
            </span>
          </>
        </MultivariatePill>
        <MultivariatePill>
          <>
            <VisaLogo className="h-10 w-10 text-[#172b85]" />
            <span className="sr-only">
              <RichText messageKey="visa" />
            </span>
          </>
        </MultivariatePill>
        <MultivariatePill>
          <>
            <MasterCardLogo className="h-6" />
            <span className="sr-only">
              <RichText messageKey="mastercard" />
            </span>
          </>
        </MultivariatePill>
        <MultivariatePill classname="bg-[#1F72CD]! px-1!">
          <>
            <AmericanExpressLogo className="h-12 w-12 text-white" />
            <span className="sr-only">
              <RichText messageKey="amex" />
            </span>
          </>
        </MultivariatePill>
      </div>
    </>
  );
};

export default MultivariatePaymentOptions;
